/**
 * Local-density (isolation) hint for point navaids, so the low-zoom declutter can
 * keep a lone mark on screen while still thinning busy clusters.
 *
 * The buoy / beacon layers gate minor marks (special, unknown) to a higher zoom so
 * a crowded harbour doesn't turn into a wall of symbols when we're zoomed right
 * out. But a single special buoy sitting on its own in open water shouldn't vanish
 * with them, because there's nothing around it to clutter. So for every mark we
 * count how many other marks sit within a short radius and stamp that count on the
 * feature. The layer filter then lets an isolated mark (few or no neighbours) show
 * at low zoom, while a mark in a cluster keeps its category gate.
 *
 * Counted per cell, which is plenty for the lone-mark case (a truly isolated mark
 * is isolated within its own cell too). A mark right on a cell seam might miss a
 * neighbour in the next cell and read as a touch more isolated than it is, which
 * only ever shows a mark slightly earlier, never hides one, so we stay on the safe
 * side.
 */
import type { ChartFeature, ChartFeatureCollection } from '@/types';

/** Marks within this many nautical miles count as neighbours. */
const NEIGHBOUR_RADIUS_NM = 1.5;

/** The mark groups we thin at low zoom, and therefore measure density across. */
const MARK_GROUPS = new Set<string>(['buoy', 'beacon']);

interface Pt {
  lon: number;
  lat: number;
  feature: ChartFeature;
}

/**
 * Stamp `attrs.neighbours` on every buoy / beacon point feature: the number of
 * other marks within NEIGHBOUR_RADIUS_NM. Mutates the collection in place (the
 * cell data is freshly fetched and owned by the caller) and is idempotent, so a
 * style swap re-running it is harmless.
 */
/**
 * Grid sizes for sounding thinning, in degrees, BY DEPTH BAND. A paper chart's
 * compiler crowds the soundings where the water is thin and spaces them out
 * where it is deep, because a metre matters in 8 m and not in 300 m. A uniform
 * grid didn't: it gave a shallow harbour bank the same sounding budget as the
 * open Caribbean next door, which read as "not enough depths in the shallows"
 * and "too many depths everywhere" in the same view (the Ponce reports, build
 * 260). So: water we could touch gets a fine grid (4x the density), mid-water
 * the old grid, and deep water a coarse one, so the deeps stop shouting.
 */
export const SOUNDING_BUCKET_SHALLOW_DEG = 0.003;
export const SOUNDING_BUCKET_MID_DEG = 0.006;
export const SOUNDING_BUCKET_DEEP_DEG = 0.012;
/** Shallower than this is "water we could touch": the fine grid. */
export const SOUNDING_SHALLOW_M = 30;
/** Deeper than this is open-water context: the coarse grid. */
export const SOUNDING_DEEP_M = 100;

/** The thinning grid size for a sounding of this depth (NaN/unknown = deep). */
export function soundingBucketDeg(depthM: number): number {
  if (depthM < SOUNDING_SHALLOW_M) return SOUNDING_BUCKET_SHALLOW_DEG;
  if (depthM <= SOUNDING_DEEP_M) return SOUNDING_BUCKET_MID_DEG;
  return SOUNDING_BUCKET_DEEP_DEG;
}

/**
 * Stamp `sdRank` on every sounding: its rank by depth, shallowest first, among
 * the soundings sharing its grid square (the grid depth-banded, above). The
 * sounding layers use it to reveal depths progressively with zoom the way a
 * paper chart's compiler would, rather than dumping the whole survey on screen
 * the moment soundings switch on, which on a dense NOAA harbour cell was a
 * wall of numbers.
 *
 * Shallowest first is the safety-correct order: when only one sounding in an
 * area can show, it must be the one that can hurt us. Deeper water is what the
 * thinned view is allowed to be vague about.
 *
 * Mutates in place and is idempotent, same contract as annotateMarkDensity.
 */
export function annotateSoundingRank(fc: ChartFeatureCollection): ChartFeatureCollection {
  const buckets = new Map<string, { f: ChartFeature; depth: number }[]>();
  for (const f of fc.features) {
    if (f.properties?.group !== 'sounding') continue;
    if (f.geometry.type !== 'Point') continue;
    const [lon, lat] = f.geometry.coordinates as [number, number];
    const attrs = f.properties.attrs as { depthM?: number } | undefined;
    // A sounding with no readable depth ranks last: never the one we keep alone.
    const depth = typeof attrs?.depthM === 'number' ? attrs.depthM : Number.POSITIVE_INFINITY;
    // The key carries the grid size too, so a shallow and a deep sounding can
    // never land in the same bucket off the backs of two different grids.
    const g = soundingBucketDeg(depth);
    const key = `${g}:${Math.floor(lon / g)}:${Math.floor(lat / g)}`;
    let bucket = buckets.get(key);
    if (!bucket) buckets.set(key, (bucket = []));
    bucket.push({ f, depth });
  }
  for (const bucket of buckets.values()) {
    bucket.sort((a, b) => a.depth - b.depth);
    bucket.forEach((entry, i) => {
      entry.f.properties.sdRank = Math.min(i, 9);
    });
  }
  return fc;
}

export function annotateMarkDensity(fc: ChartFeatureCollection): ChartFeatureCollection {
  const pts: Pt[] = [];
  for (const f of fc.features) {
    if (!MARK_GROUPS.has(f.properties.group)) continue;
    const g = f.geometry;
    if (g.type !== 'Point') continue;
    const [lon, lat] = g.coordinates;
    if (typeof lon !== 'number' || typeof lat !== 'number') continue;
    pts.push({ lon, lat, feature: f });
  }

  if (pts.length === 0) return fc;

  // Bucket into a grid at least one neighbour-radius across, then compare each
  // mark only with the marks in its own bucket and the eight around it. Two
  // marks within the radius cannot land further apart than that, so the answer
  // is identical to comparing everything with everything.
  //
  // It used to compare everything with everything, and that is what took the
  // app down whenever Edd panned to America. On a Scottish training cell with
  // three hundred marks it is ninety thousand comparisons and instant; on a
  // dense American cell with twenty thousand marks it is four HUNDRED MILLION,
  // synchronously, on the main thread, inside the cell load. The symptoms all
  // followed from that one line: the app froze mid-pan, sat on its boot screen
  // (the overlay's own timeout could not fire, because nothing could), and
  // Android reclaimed a renderer that had stopped answering — while the heap
  // read 14 MB, because this was never about memory. The sounding ranker above
  // has bucketed like this all along.
  const latDeg = NEIGHBOUR_RADIUS_NM / 60;
  // Longitude degrees shrink with latitude, so size the grid on the highest
  // latitude in the cell: buckets come out wider than needed further south,
  // which costs a few extra comparisons and can never miss a neighbour.
  let maxAbsLat = 0;
  for (const p of pts) maxAbsLat = Math.max(maxAbsLat, Math.abs(p.lat));
  const cosMin = Math.max(Math.cos((maxAbsLat * Math.PI) / 180), 0.01);
  const lonDeg = latDeg / cosMin;

  const grid = new Map<string, Pt[]>();
  for (const p of pts) {
    const key = `${Math.floor(p.lon / lonDeg)}:${Math.floor(p.lat / latDeg)}`;
    let bucket = grid.get(key);
    if (!bucket) grid.set(key, (bucket = []));
    bucket.push(p);
  }

  for (const p of pts) {
    const cosLat = Math.cos((p.lat * Math.PI) / 180);
    const bx = Math.floor(p.lon / lonDeg);
    const by = Math.floor(p.lat / latDeg);
    let neighbours = 0;
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        const bucket = grid.get(`${bx + dx}:${by + dy}`);
        if (!bucket) continue;
        for (const q of bucket) {
          if (q === p) continue;
          // Equirectangular nm distance, good enough at these short ranges.
          const dLatNm = (q.lat - p.lat) * 60;
          const dLonNm = (q.lon - p.lon) * 60 * cosLat;
          if (Math.hypot(dLatNm, dLonNm) <= NEIGHBOUR_RADIUS_NM) neighbours++;
        }
      }
    }
    p.feature.properties.attrs.neighbours = neighbours;
  }

  return fc;
}
